import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';
import { SUGGESTIONS, renderMarkdown } from './ChatWidget';

/**
 * Full-page chat route — used on mobile (and accessible on desktop too).
 *
 * Why this exists alongside the floating ChatWidget overlay: iOS Safari cannot
 * reliably handle `position: fixed` overlays once the soft keyboard opens —
 * the visual viewport jumps, scroll containment leaks, and the input bar gets
 * pushed off-screen. A real page route avoids all of that: native scrolling,
 * native keyboard handling, no overlay layering tricks.
 *
 * Conversation state is local to this component (per the design spec).
 * Conversations don't persist between the mobile page and the desktop widget.
 */
export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState(null);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, thinking]);

  // Role gate — chat is for agents/brokers/super_admins. Redirect anyone else home.
  if (!user) return <Navigate to="/" replace />;
  if (!['super_admin', 'broker', 'agent'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || thinking) return;

    const history = messages;
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setInput('');
    setThinking(true);
    setError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const res = await authFetch(`${API}/api/chat`, {
        method: 'POST',
        body: JSON.stringify({ message: trimmed, conversation: history }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = await res.json();
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: data.response || '' },
      ]);
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Sorry, I couldn't process that just now. Please try again in a moment.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    const maxHeight = 24 * 3 + 16; // ~3 rows
    el.style.height = `${Math.min(el.scrollHeight + 2, maxHeight)}px`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0E27]">
      {/* Header — sticky so the Back link stays accessible as the conversation scrolls */}
      <header
        className="sticky top-0 z-10 flex items-center justify-between px-3 py-3 border-b shrink-0"
        style={{ backgroundColor: '#1A2744', borderColor: 'rgba(84,110,122,0.3)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#00D4FF] hover:text-[#00E676] text-sm font-medium px-2 py-2 -ml-2 min-h-[44px]"
          aria-label="Back"
        >
          ← <span>Back</span>
        </button>
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: '#FF6B35' }}
          />
          <span className="text-white font-semibold text-sm">SchemeSync AI</span>
        </div>
        {/* Spacer for visual balance opposite the Back button */}
        <span className="w-12" aria-hidden="true" />
      </header>

      {/* Messages — flex-1 takes all remaining space, natural overflow scrolling */}
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col gap-2 w-full max-w-full">
            <p className="text-gray-400 text-xs mb-1">
              Tap a suggestion to get started:
            </p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="w-full text-left px-3 py-3 rounded-lg text-sm border transition-colors hover:border-[#00D4FF] truncate min-w-0"
                style={{
                  backgroundColor: 'rgba(10,14,39,0.7)',
                  borderColor: 'rgba(0,212,255,0.3)',
                  color: '#00D4FF',
                  maxWidth: '100%',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((msg, i) =>
          msg.role === 'user' ? (
            <div
              key={i}
              className="ml-auto max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm border text-white text-sm bg-[#FF6B35]/15 border-[#FF6B35]/30"
            >
              {msg.content}
            </div>
          ) : (
            <div
              key={i}
              className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#1A2744] border-[#546E7A]/30"
            >
              {renderMarkdown(msg.content)}
            </div>
          )
        )}

        {thinking && (
          <div className="mr-auto max-w-[90%] px-3 py-2 rounded-2xl rounded-tl-sm border text-gray-200 text-sm bg-[#1A2744] border-[#546E7A]/30">
            <span className="inline-flex gap-0.5">
              <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
              <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
              <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
            </span>
          </div>
        )}

        <div ref={bottomRef} />
      </main>

      {error && (
        <div className="px-4 py-1.5 text-xs text-red-300 bg-red-900/20 border-t border-red-500/20">
          {error}
        </div>
      )}

      {/* Input bar — sits at the natural bottom of the flex column. iOS handles the
          soft keyboard by scrolling the input into view automatically since this is
          a plain page (not a fixed overlay), so no visualViewport hacks needed. */}
      <footer
        className="flex items-end gap-2 px-3 py-2 border-t shrink-0 w-full max-w-full"
        style={{ backgroundColor: '#1A2744', borderColor: 'rgba(84,110,122,0.3)' }}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything..."
          className="flex-1 min-w-0 resize-none rounded-lg px-3 py-2 text-base text-white placeholder-gray-500 border focus:outline-none focus:border-[#00D4FF]/60"
          style={{
            backgroundColor: '#0A0E27',
            borderColor: 'rgba(84,110,122,0.5)',
            maxHeight: '96px',
          }}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || thinking}
          aria-label="Send"
          className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-white text-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
          style={{ backgroundColor: '#FF6B35' }}
        >
          <span>➤</span>
        </button>
      </footer>
    </div>
  );
}
